import React, { useMemo, useState } from "react";
import {
  Box,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { getFormSectionStyles } from "./formSectionStyles";

interface ClientProcessConfig {
  [key: string]: any;
}

interface ClientProcessFormProps {
  config: ClientProcessConfig;
  handleChange: (path: string, value: any) => void;
}

interface FieldGroup {
  /** Optional eyebrow label shown above the group. Omit for an ungrouped block. */
  title?: string;
  /** Optional one-line helper rendered under the title. */
  description?: string;
  fields: string[];
}

const tabSections = [
  "Settings",
  "Phone Sync",
  "Auto Scan",
  "Leg Fold",
  "Phone Camera",
  "Preferences",
  "Wizard",
  "Overlay Order",
];

/** Visual grouping of fields per tab. Each group renders with its own eyebrow
 *  label and renders fields with shared spacing. Switch fields and scalar
 *  fields can mix freely inside a group — the renderer picks the right control
 *  based on the value's type. */
const tabLayouts: Record<string, FieldGroup[]> = {
  Settings: [
    {
      title: "Environment & URLs",
      fields: ["url", "name", "s3_bucket", "ums_environment", "location_id"],
    },
    {
      title: "VIN configuration",
      fields: ["vin_length", "vin_regex", "prefix_removal"],
    },
    {
      title: "Feedback terminology",
      description: "Strings shown to operators during scan failure / retry flows.",
      fields: [
        "vin_scan_terminology",
        "vin_feedback_terminology",
        "vin_length_err_terminology",
      ],
    },
    {
      title: "Feature toggles",
      fields: ["is_default", "is_display_tablet_rooftop", "use_lighting_controller"],
    },
  ],
  "Phone Sync": [
    {
      fields: ["phone_sync_terminology"],
    },
    {
      title: "Toggles",
      fields: ["phone_sync"],
    },
  ],
  "Auto Scan": [
    {
      title: "Timing",
      fields: [
        "auto_scan_countdown_length",
        "manual_scan_countdown_length",
        "display_screen_sleep_timer",
      ],
    },
    {
      title: "Behaviour toggles",
      fields: [
        "auto_scan_enabled",
        "auto_scan_pano",
        "blackout_screen_during_capture",
        "tablet_rooftop_select",
        "skip_interior_simultaneous",
      ],
    },
  ],
  "Leg Fold": [
    {
      description: "Timing controls for actuator leg fold and stagger.",
      fields: [
        "actuator_fold_trigger_distance",
        "actuator_leg_stagger_time",
        "actuator_trigger_count_threshold",
        "actuator_leg_cycle_time",
      ],
    },
  ],
  "Phone Camera": [
    {
      fields: ["phone_camera_resolution", "phone_camera_lens"],
    },
  ],
  Preferences: [
    {
      title: "Behaviour",
      fields: [
        "use_nhtsa_database",
        "allow_cancel_vehicle",
        "dual_hand_scan_capture",
        "preferances.skipExternal",
        "preferances.skipPano",
      ],
    },
    {
      title: "VIN preferences",
      fields: ["preferances.vinchar", "preferances.vinBypass"],
    },
  ],
  Wizard: [
    {
      description: "Wizard step lists. Editable as JSON.",
      fields: ["enabled", "disabled"],
    },
  ],
  "Overlay Order": [
    {
      description: "Overlay sequence used by the carousel.",
      fields: ["overlays"],
    },
  ],
};

const TAB_DESCRIPTIONS: Record<string, string> = {
  Settings: "Core configuration values that identify this config and shape its display behaviour.",
  "Phone Sync": "How the mobile capture device communicates with this configuration.",
  "Auto Scan": "Timing and behaviour for the automated scanning flow.",
  "Leg Fold": "Actuator timing parameters that drive the unit's leg fold sequence.",
  "Phone Camera": "Phone-camera hardware preferences applied during capture.",
  Preferences: "Behavioural preferences and VIN handling rules.",
  Wizard: "Steps enabled or disabled in the operator wizard flow.",
  "Overlay Order": "Display order of overlays used downstream of the configuration.",
};

const ACRONYMS = new Set([
  "VIN",
  "URL",
  "S3",
  "API",
  "UMS",
  "NHTSA",
  "ID",
  "IP",
]);

const NUMERIC_FIELDS = new Set([
  "location_id",
  "vin_length",
  "prefix_removal",
  "auto_scan_countdown_length",
  "manual_scan_countdown_length",
  "display_screen_sleep_timer",
  "actuator_fold_trigger_distance",
  "actuator_leg_stagger_time",
  "actuator_trigger_count_threshold",
  "actuator_leg_cycle_time",
]);

// Fields that are visible but not user-editable here. The actual value is
// owned somewhere else (e.g. the system-level record or the device's ENV var).
const READ_ONLY_FIELDS: Record<string, string> = {
  ums_environment: "Sourced from the system-level environment and the device's ENV var. Not editable per config.",
};

/** Turn "vin_scan_terminology" -> "VIN Scan Terminology" and respect known acronyms. */
const humanizeFieldKey = (fieldKey: string) => {
  const last = fieldKey.split(".").pop() ?? fieldKey;
  return last
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => {
      const upper = word.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const readPath = (config: ClientProcessConfig, fieldKey: string) =>
  fieldKey.split(".").reduce<any>((obj, k) => obj?.[k], config);

const isNumericField = (key: string, value: any) =>
  NUMERIC_FIELDS.has(key) || typeof value === "number";

const ClientProcessForm: React.FC<ClientProcessFormProps> = ({ config, handleChange }) => {
  const theme = useTheme();
  const styles = getFormSectionStyles(theme);
  const [tabIndex, setTabIndex] = useState(0);

  const activeTab = tabSections[tabIndex];
  const layout = useMemo<FieldGroup[]>(() => tabLayouts[activeTab] ?? [], [activeTab]);

  const renderScalarField = (fieldKey: string, value: any) => {
    const type = typeof value;
    const label = humanizeFieldKey(fieldKey);
    const readOnlyHelper = READ_ONLY_FIELDS[fieldKey];

    if (type === "number" || type === "string" || value === null || value === undefined) {
      const numeric = isNumericField(fieldKey, value);
      return (
        <TextField
          key={fieldKey}
          fullWidth
          label={label}
          value={value ?? ""}
          type="text"
          disabled={Boolean(readOnlyHelper)}
          helperText={readOnlyHelper}
          inputProps={numeric ? { inputMode: "numeric", pattern: "[0-9]*" } : undefined}
          onChange={(event) => handleChange(fieldKey, event.target.value)}
          sx={styles.inputField}
        />
      );
    }

    // Nested object / array — editable as JSON
    return (
      <TextField
        key={fieldKey}
        fullWidth
        multiline
        minRows={4}
        maxRows={16}
        label={label}
        value={JSON.stringify(value, null, 2)}
        onChange={(event) => {
          try {
            const parsed = JSON.parse(event.target.value);
            handleChange(fieldKey, parsed);
          } catch {
            // ignore malformed JSON until it parses
          }
        }}
        sx={styles.inputField}
      />
    );
  };

  const renderToggleRow = (fieldKey: string, value: boolean) => {
    const label = humanizeFieldKey(fieldKey);
    return (
      <Box key={fieldKey} sx={styles.toggleRow}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
            {label}
          </Typography>
        </Box>
        <FormControlLabel
          sx={{ m: 0 }}
          label=""
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(event) => handleChange(fieldKey, event.target.checked)}
              sx={styles.switchControl}
            />
          }
        />
      </Box>
    );
  };

  const renderGroup = (group: FieldGroup, groupIndex: number) => {
    const groupFields = group.fields
      .map((fieldKey) => ({ fieldKey, value: readPath(config, fieldKey) }))
      .filter(({ value }) => value !== undefined || true); // keep undefined values; treat as empty

    const scalars = groupFields.filter(({ value }) => typeof value !== "boolean");
    const toggles = groupFields.filter(({ value }) => typeof value === "boolean");

    return (
      <Stack key={`${group.title ?? "block"}-${groupIndex}`} spacing={2}>
        {(group.title || group.description) && (
          <Stack spacing={0.5}>
            {group.title && <Typography sx={styles.eyebrow}>{group.title}</Typography>}
            {group.description && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {group.description}
              </Typography>
            )}
          </Stack>
        )}

        {scalars.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            {scalars.map(({ fieldKey, value }) => (
              <Box
                key={fieldKey}
                sx={{
                  // Multi-line JSON fields take the full row width.
                  gridColumn:
                    value !== null &&
                    value !== undefined &&
                    typeof value !== "string" &&
                    typeof value !== "number"
                      ? { xs: "1 / -1", sm: "1 / -1" }
                      : undefined,
                }}
              >
                {renderScalarField(fieldKey, value)}
              </Box>
            ))}
          </Box>
        )}

        {toggles.length > 0 && (
          <Stack spacing={1}>
            {toggles.map(({ fieldKey, value }) => renderToggleRow(fieldKey, Boolean(value)))}
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <Box sx={{ ...styles.surfaceCard, p: { xs: 2.5, md: 3.5 } }}>
      {/* Segmented tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, next) => setTabIndex(next)}
          variant="scrollable"
          scrollButtons="auto"
          sx={styles.segmentedTabs}
        >
          {tabSections.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Box>

      {/* Tab header */}
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography sx={styles.eyebrow}>Section</Typography>
        <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: "-0.005em" }}>
          {activeTab}
        </Typography>
        {TAB_DESCRIPTIONS[activeTab] && (
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 720 }}>
            {TAB_DESCRIPTIONS[activeTab]}
          </Typography>
        )}
      </Stack>

      {/* Field groups */}
      <Stack spacing={3.5} divider={<Divider sx={{ borderColor: styles.surfaceDivider }} />}>
        {layout.map((group, index) => renderGroup(group, index))}
      </Stack>
    </Box>
  );
};

export default ClientProcessForm;
